import Head from 'next/head';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import {useTranslation} from 'next-i18next';
import type {GetServerSideProps} from 'next';
import {AnimatePresence} from 'motion/react';
import {QueryClient} from '@tanstack/react-query';
import {BackgroundGradient, MainTitle, LandingBigButton} from 'src/components/landingStyledComponents';
import {WidthContainer} from 'src/components/landing/LandingComponents';
import useLandingContactModal from 'src/utils/useLandingContactModal';
import LandingHeader from 'src/components/landing/LandingHeader';
import LandingFooter from 'src/components/landing/LandingFooter';
import {getPublicLayout} from 'src/components/PublicLayout';
import {nsTranslation, nsWebsite} from 'src/services/i18n';
import RowOfLogos from 'src/pages/landing-page/RowOfLogos';
import ssrTranslations from 'src/utils/ssrTranslations';
import getTitle from 'src/utils/getTitle';

const LandingContactFormModal = dynamic(() => import('src/pages/landing-page/LandingContactFormModal'), {ssr: false});

const mobileThreshold = 600;

const ContentEN = () => (
  <ContentContainer>
    <MainTitle className="mb-16">
      Looking for another one of us.{' '}
      <span style={{color: 'var(--panelista-color)'}}>Full-stack JavaScript developer.</span>
    </MainTitle>

    <p>
      <strong>We help companies to involve their customers.</strong> In a real, personal way. Panelista is a digital
      service where companies ask their customers video questions, and get fast high-quality feedback that turns into
      conversations.
    </p>

    <p>
      We are a group of experienced product leaders, developers, designers and business developers. We have run startups
      and innovation teams, helped large organizations become agile, and developed some of the digital products that you
      encounter in your everyday life.
    </p>

    <p style={{marginBottom: 50}}>And from time to time, we want to expand our team, so we can do it better.</p>

    <RowOfLogos />

    <h2 style={{marginTop: 50}}>Developing Panelista</h2>

    <p>
      <strong>We have a main focus when we build Panelista, and that is quality.</strong> We want to build software we
      are proud of. Proud of its user experience, speed, design, architecture, reliability, and all other aspects that
      reduce friction. We want the results of our work to be so good that we are too excited not to share it with our
      friends.{' '}
      <i>
        And even better, we want Panelista to create so much value for the users that they will have the need to share
        that experience with others, too.
      </i>{' '}
      We want to create something that is a joy to use, and a joy to build.
    </p>

    <p>
      Panelista is a young company and a small team (6 people right now) where everyone has a big impact on the whole.
    </p>

    <h2 style={{color: 'var(--panelista-color)', paddingTop: 120, marginTop: -80}} id="full-stack">
      <a href="#full-stack">Full-stack Javascript developer</a>
    </h2>

    <p>
      Your work will touch most parts of the web app. A wide breadth of functionality - from backend API to React
      components, UI animations, custom embedded widgets, some machine learning, text analysis and more.
    </p>

    <p>
      We don't expect you to have experience in every technology we use. But we do expect you to have a habit of
      learning. New challenges sometimes ask for new solutions, and we are a compact team with high ambitions, which is
      why we often explore and learn in order to build the things we want to build.
    </p>

    <h3 style={{marginBottom: 20}}>What makes you a good fit</h3>

    <ul>
      <li>You care deeply about the quality of the work you do.</li>
      <li>You have experience in building a complete software product.</li>
      <li>
        You have a good understanding of modern frontend architecture (data fetching, data flow, rendering performance).
      </li>
      <li>You are a skilled problem solver.</li>
      <li>You are a clear communicator.</li>
      <li>
        You live fairly close to our nice office in downtown Gothenburg, Sweden. If not but you think you are a great
        fit, apply anyway.
      </li>
    </ul>

    <p>
      <strong>Tech</strong>
    </p>
    <ul>
      <li>Typescript</li>
      <li>NextJS (NodeJS)</li>
      <li>React, Tailwind</li>
      <li>MongoDB</li>
      <li>TanStack Query</li>
      <li>Github, Slack, Linear</li>
    </ul>

    <h3>What we offer</h3>
    <p>
      You will be joining me (Jensa), multiplying the size of the product team. I am one of the co-founders and a
      full-stack developer myself, building Panelista with the values and technologies above, enjoying the practical
      benefits below.
    </p>

    <ul className="spread-list">
      <li>
        <strong>Diverse challenges.</strong>
        <br />
        The product touches several tech fields and you will work on many of them. We are also a small team and everyone
        has an impact overreaching their discipline. You won't be isolated in the product development silo.
      </li>
      <li>
        <strong>Work life balance.</strong>
        <br />
        We respect our time outside of work and make the time at work fit our needs. To work together is the norm but we
        work remotely / from home whenever we need to.
      </li>
      <li>
        <strong>Fun.</strong>
        <br />
        We try to mix things up from time to time and work together from nice remote places, do quizzes and eat
        breakfast on Tuesdays. Some of us are also very funny, which increases the average fun levels.
      </li>
      <li>
        <strong>Autonomy.</strong>
        <br />
        To do your work how you see fit. No time reporting. Be part of the decision process and planning, choose the
        tools & libraries your work with. We respect our time at work and trust each other.
      </li>
      <li>
        <strong>Money.</strong>
        <br />
        Fair pay, pension, employee equity program and other benefits.
      </li>
      <li>
        <strong>Tools.</strong>
        <br />
        MacBook Pro, display and accessories.
      </li>
    </ul>

    <div>
      <LandingBigButton style={{marginTop: 20, marginBottom: 100}} href="mailto:jensa@panelista.com">
        Apply by sending me an email so that we can schedule something&nbsp; 👋
      </LandingBigButton>
    </div>
  </ContentContainer>
);

const ContentSV = () => (
  <ContentContainer>
    <MainTitle className="mb-16">
      Är du vår nya kollega?{' '}
      <span style={{color: 'var(--panelista-color)'}}>Vi söker en Full-stack JavaScript-utvecklare.</span>
    </MainTitle>

    <p>
      <strong>Vi hjälper företag att involvera sina kunder.</strong> På ett personligt och snabbrörligt sätt. Panelista
      är en digital tjänst där företag ställer videofrågor till sina kunder och får kontinuerlig feedback av hög
      kvalitet.
    </p>

    <p style={{marginBottom: 50}}>Nu vill vi hitta en ny kollega för att kunna göra vår produkt ännu bättre.</p>

    <RowOfLogos />

    <p>
      <strong>Kvalitet är vårt huvudfokus.</strong> Vi vill bygga en produkt som vi är riktigt stolta över. Det gäller
      alla delar i Panelista, som användarupplevelse, hastighet, design, arkitektur, tillförlitlighet och allt som
      minskar friktion.
    </p>

    <p>Panelista är ett ungt företag med ett tajt team på 6 personer, där alla har stor påverkan på helheten.</p>

    <h2 style={{color: 'var(--panelista-color)', paddingTop: 120, marginTop: -80}} id="full-stack">
      <a href="#full-stack">Full-stack Javascript-utvecklare</a>
    </h2>

    <p>
      Ditt arbete kommer att beröra de flesta delar av webbappen. Det betyder en stor bredd av funktionalitet, från
      backend-API till React-komponenter, UI-animationer, anpassade inbäddade widgets, viss maskininlärning, textanalys
      och annat.
    </p>

    <p>
      Vi förväntar oss inte att du har erfarenhet av all teknik vi använder. Men vi förväntar oss att du är nyfiken och
      van att lära dig nya saker. Nya utmaningar kräver ibland nya lösningar och vi är ett litet team med stora
      ambitioner, där vi ofta utforskar och lär oss för att kunna bygga det vi vill.
    </p>

    <h3 style={{marginBottom: 20}}>Vi tror att du känner igen dig i dessa saker</h3>

    <ul>
      <li>Du bryr dig mycket om kvaliteten på det du gör</li>
      <li>Du har erfarenhet av att bygga en komplett mjukvaruprodukt</li>
      <li>Du har god förståelse för modern frontend-arkitektur (datahämtning, dataflöden, renderingsprestanda)</li>
      <li>Du är en skicklig problemlösare</li>
      <li>Du är nyfiken och vill lära dig mycket nytt</li>
      <li>Du är bra på att kommunicera</li>
      <li>
        Du bor ganska nära vårt fina kontor i centrala Göteborg. Om inte, men du tycker att det låter intressant, ansök
        ändå.
      </li>
    </ul>

    <p>
      <strong>Tech</strong>
    </p>
    <ul>
      <li>Typescript</li>
      <li>NextJS (NodeJS)</li>
      <li>React, Tailwind</li>
      <li>MongoDB</li>
      <li>TanStack Query</li>
      <li>Github, Slack, Linear</li>
    </ul>

    <h3>Hur det är att jobba med oss</h3>
    <p>
      Du kommer att jobba i produktteamet med mig (Jensa) som skrivit den här beskrivningen. Jag är också en
      full-stack-utvecklare och en av grundarna till Panelista. Du kommer att få möjlighet att utvecklas, ta mycket
      ansvar och lära dig av hur hela Panelista-teamet jobbar tillsammans i en kreativ och generös miljö.
    </p>

    <ul className="spread-list">
      <li>
        <strong>Breda och roliga utmaningar</strong>
        <br />
        Produkten berör flera tekniska områden och du kommer att arbeta med många av dem. Vi är ett litet team så det
        blir en kombination av att få vara expert och få jobba brett dvs. hugga tag i det mesta.
      </li>
      <li>
        <strong>Balans i arbetslivet</strong>
        <br />
        Vi respekterar vår tid utanför arbetet. Vi jobbar på jobbet och ser till att ha tid för reflektion och
        återhämtning. Att arbeta tillsammans är normen men vi arbetar på distans/hemifrån när vi behöver. Vi har kul
        ihop och delar både små och stora framgångar och motgångar. Vi litar på varandra - alla får ta mycket ansvar,
        testa nya grejer, och ingen rapporterar tid.
      </li>
      <li>
        <strong>Ersättning</strong>
        <br />
        Rättvis lön, pension, delägande för anställda och andra bra förmåner.
      </li>
      <li>
        <strong>Verktyg</strong>
        <br />
        MacBook Pro, display och tillbehör.
      </li>
    </ul>

    <div>
      <LandingBigButton style={{marginTop: 20, marginBottom: 100}} href="mailto:jensa@panelista.com">
        Ansök genom att skicka ett mail till mig så bokar vi in något&nbsp; 👋
      </LandingBigButton>
    </div>
  </ContentContainer>
);

export const ContentContainer = styled(WidthContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: var(--landing-header-height);
  margin-top: 100px;
  position: relative;

  p,
  ul {
    font-size: 18px;
    width: 100%;
    max-width: 800px;
  }

  ul {
    list-style: disc;
    margin-bottom: 14px;

    &.spread-list {
      li {
        margin-top: 10px;
        margin-bottom: 20px;
      }
    }

    li {
      margin-left: 20px;
      margin-bottom: 6px;
    }
  }

  p {
    margin: 14px 20px;
  }

  h2,
  h3 {
    width: 100%;
    max-width: 800px;
    font-family: var(--font-jakarta);
    font-weight: 700;
    line-height: 1.1;
  }

  h2 {
    font-size: 36px;
    letter-spacing: -0.015em;
    margin-top: 40px;
    margin-bottom: 16px;

    a {
      color: inherit;
      text-decoration: none;
    }
  }

  h3 {
    font-size: 22px;
    margin-top: 24px;
  }

  ${MainTitle} {
    text-align: center;
    max-width: 800px;

    @media (max-width: ${mobileThreshold}px) {
      text-align: left;
    }
  }

  @media (max-width: 900px), (max-height: 800px) {
    margin-top: 40px;
  }

  @media (max-height: 800px) {
    margin-bottom: -40px;
  }
`;

const pageTitleEn = 'Looking for one of us. Full-stack JavaScript developer.';
const pageTitleSv = 'Vi vill bli fler. Full-stack JavaScript-utvecklare.';

const descriptionEn =
  'We help companies to involve their customers. In a real, personal way. Panelista is a digital service where companies ask their customers video questions, and get high-quality feedback that turns into conversations.';

const descriptionSv =
  'Vi hjälper företag att involvera sina kunder, på ett personligt sätt. Panelista är en digital tjänst där företag ställer videofrågor till sina kunder och får kontinuerlig och agerbar feedback av hög kvalitet.';

const CareersPage = () => {
  const {i18n} = useTranslation();

  const pageTitle = i18n.language === 'en' ? pageTitleEn : pageTitleSv;
  const description = i18n.language === 'en' ? descriptionEn : descriptionSv;

  const {isModalOpened, modalType, closeModal, openModalOfType} = useLandingContactModal();

  return (
    <>
      <Head>
        <title>{getTitle(pageTitle)}</title>
        <meta key="og:title" content={getTitle(pageTitle)} property="og:title" />
        <meta name="description" content={description} key="description" />
        <meta key="og:description" content={description} property="og:description" />

        <meta
          key="og:image"
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_PRODUCTION_APP_URL}/images/og-careers.jpg`}
        />
      </Head>

      <AnimatePresence>
        {isModalOpened ? <LandingContactFormModal closeModal={closeModal} extraFormData={{type: modalType}} /> : null}
      </AnimatePresence>

      <BackgroundGradient />

      <LandingHeader />

      {i18n.language === 'en' ? <ContentEN /> : <ContentSV />}

      <LandingFooter />
    </>
  );
};

CareersPage.isPublicPage = true;

CareersPage.getLayout = getPublicLayout;

export const getServerSideProps: GetServerSideProps = async (context) => {
  // Uncomment the following line to bring Careers page back online
  // return {notFound: true};

  const queryClient = new QueryClient();

  return {props: {...(await ssrTranslations(context, queryClient, [nsTranslation, nsWebsite]))}};
};

export default CareersPage;
